import { useState } from "react";
import { useKnowledgeBase } from "../lib/useKnowledgeBase";
import { isAppAdmin, useMe } from "../lib/useMe";

// Sidebar control for the runtime-cloned System Knowledge Base
// (omni-system-knowledge-base), which provides ALL served content (docs, devhub,
// skills, repos). Only Omni Hub admins can clone/update it; everyone else sees a
// read-only status. When content isn't mapped yet, admins get a "click to clone"
// affordance; once present it shows a live status (branch · commit) and admins can
// re-sync. State streams over SSE, so a clone kicked off anywhere updates every client.
export default function KnowledgeBaseNav() {
  const me = useMe();
  const admin = isAppAdmin(me);
  const { status, clone } = useKnowledgeBase();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (!status) return null; // still loading — don't flash a "not set up" state

  // The System Knowledge Base is Incode-owned, so syncing it needs INCODE_GH_TOKEN
  // (plus gh + a writable clone dir) — that's exactly what status.canClone reports.
  // When it can't be synced AND nothing is mapped yet (e.g. a deploy with no
  // INCODE_GH_TOKEN), don't offer the sync control at all. If content is already
  // present we keep the read-only status so users can see what's mapped.
  if (!status.canClone && !status.present) return null;

  const cloning = status.phase === "cloning" || busy;
  const present = status.present;
  const errored = status.phase === "error" || !!err;

  // Only admins can trigger a clone/update, and only when the container can clone.
  const clickable = admin && !cloning && status.canClone;
  const setup = !present && clickable; // highlight the actionable "set it up" state

  const dot = cloning ? "syncing" : present ? "ok" : errored ? "error" : "idle";

  async function onClone() {
    if (!clickable) return;
    setErr(null);
    setBusy(true);
    const e = await clone();
    setBusy(false);
    if (e) setErr(e);
  }

  // Second line under the label (hidden when the rail is collapsed).
  const detail = err
    ? err
    : cloning
      ? "Updating…"
      : present
        ? [status.branch, status.commit].filter(Boolean).join(" · ") || "Mapped"
        : admin
          ? status.canClone
            ? "Not set up — click to clone"
            : status.reason || "Unavailable"
          : "Not set up — ask an admin";

  const title = [
    `System Knowledge Base: ${status.repo ?? "unknown"}${status.ref ? ` @ ${status.ref}` : ""}`,
    present ? `Mapped at ${status.systemRoot}` : "Not cloned yet",
    status.commit ? `Commit ${status.commit}` : null,
    status.reason ? `⚠ ${status.reason}` : null,
    clickable ? (present ? "Click to update" : "Click to clone") : admin ? null : "Admins only",
  ]
    .filter(Boolean)
    .join("\n");

  return (
    <div className={`nav-kb${setup ? " nav-kb-setup" : ""}`}>
      <button
        className="nav-link nav-kb-btn"
        onClick={onClone}
        disabled={!clickable}
        title={title}
        aria-busy={cloning}
      >
        <span className="nav-link-label">System Knowledge Base</span>
      </button>
      <div className={`nav-kb-detail${errored ? " kb-err" : ""}`}>
        <span className="nav-kb-detail-text">{detail}</span>
        <span className={`kb-dot kb-dot-${dot}`} aria-hidden="true" title="Status" />
      </div>
    </div>
  );
}
